import { Box, Flex, Text } from "@biblioteksentralen/react";
import { CampaignViewSection } from "../campaigns/CampaignViewSection";
import { useTranslation } from "../../utils/hooks/useTranslation";
import { ResolvedSommerles } from "@libry-content/common";
import { SanityImagePreservingAspectRatio } from "../images/SanityImagePreservingAspectRatio";
import { Limelight } from "next/font/google";
import { ReactNode } from "react";

const limelightFont = Limelight({ subsets: ["latin"], weight: "400" });

type Stats = {
  avgLevel: number;
  books: number;
  pages: number;
  participants: number;
  prizes: number;
  trophies: number;
  year: number;
};

export type SommerlesApiStats = { thisYear: Stats; lastYear: Stats };

const numberWithSpaces = (number: number) => number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ");

const Bold = (children: ReactNode) => (
  <Text as="span" fontWeight="bold">
    {children}
  </Text>
);

// Denne funksjonen brukes til å beregne gjeldende kampanjedag (1. juni er dag 1, 1. juni er dag 31 osv.)
const getCurrentSommerlesDayNumber = () => {
  const now = new Date();
  const startOfSommerles = new Date(now.getFullYear(), 5, 0);
  const diff = now.getTime() - startOfSommerles.getTime();
  const oneDay = 1000 * 60 * 60 * 24;
  const dayNumber = Math.floor(diff / oneDay);
  return dayNumber;
};

type Props = {
  sommerles: ResolvedSommerles;
  sommerlesApiStats?: SommerlesApiStats;
};

export const SommerlesStats = ({ sommerles, sommerlesApiStats }: Props) => {
  const { t, tRich } = useTranslation();

  if (!sommerlesApiStats) return null;

  const detail4 = sommerles.templateContent?.detail4;
  const detail9 = sommerles.templateContent?.detail9;
  const detail10 = sommerles.templateContent?.detail10;
  const detail11 = sommerles.templateContent?.detail11;
  const character1 = sommerles.templateContent?.character1Image;
  const character2 = sommerles.templateContent?.character2Image;

  const { lastYear, thisYear } = sommerlesApiStats;

  const sommerlesTotalDays = 92;
  const sommerlesCurrentDay = getCurrentSommerlesDayNumber();
  const sommerlesDaysLeft = sommerlesTotalDays - sommerlesCurrentDay;
  // Don't render statistic component when current number of participants is less than 25% of last years participants, because the numbers displayed become very disproportionate
  const enoughParticipants = sommerlesApiStats.thisYear.participants / sommerlesApiStats.lastYear.participants > 0.25;

  if (
    sommerlesCurrentDay <= 0 ||
    sommerlesCurrentDay >= sommerlesTotalDays ||
    lastYear.participants <= 0 ||
    thisYear.participants <= 0 ||
    !enoughParticipants
  )
    return null;

  const pagesReadThisYear = thisYear.pages;
  const pagesReadLastYear = lastYear.pages;
  const pagesReadDelta = pagesReadLastYear - pagesReadThisYear;
  const currentProgressPercentage = (pagesReadThisYear / pagesReadLastYear) * 100;
  const dailyPagesLastYear = Math.round(pagesReadLastYear / (lastYear.participants * sommerlesTotalDays));
  const dailyPagesThisYear = Math.round(pagesReadThisYear / (thisYear.participants * sommerlesCurrentDay));
  const dailyPagesToWin = Math.ceil(
    (pagesReadLastYear - pagesReadThisYear) / (thisYear.participants * sommerlesDaysLeft)
  );
  const dailyPagesDelta = dailyPagesToWin - dailyPagesThisYear;

  return (
    <CampaignViewSection headline={t("Klarer dere å lese mer enn i fjor?")} zIndex={1}>
      {pagesReadDelta > 0 ? (
        <Text>
          <Text as="span">
            {tRich("I fjor leste barn fra vårt bibliotek <Bold>{siderLestIFjor} sider</Bold>", {
              siderLestIFjor: numberWithSpaces(pagesReadLastYear),
              Bold,
            })}
            {". "}
          </Text>
          <Text as="span">
            {tRich("I år har dere lest <Bold>{siderLestIÅr} sider</Bold>", {
              siderLestIÅr: numberWithSpaces(pagesReadThisYear),
              Bold,
            })}
            {". "}
          </Text>
          <Text as="span">{t("Hjelp Astrid og Asbjørn i mål")}!</Text>
        </Text>
      ) : (
        <Text>{`${t("Dere har offisielt lest flere sider enn i fjor")}. ${t(
          "Astrid og Asbjørn har nådd målet!"
        )}`}</Text>
      )}
      <Box
        className={limelightFont.className}
        css={{ "& h1,h2": limelightFont.style }}
        width="100%"
        display="flex"
        alignItems="center"
        justifyContent="flex-start"
        color="#6D1F49"
        margin="2rem 0 5rem"
        aria-hidden
      >
        <Flex flexDirection="column">
          <Box height="7rem" marginTop="2rem" border={`1px solid #6D1F49`}></Box>
          <Box display="flex" flexDirection="column" alignItems="flex-start" justifyContent="flex-start">
            <Text position="absolute" fontSize="2xl">
              {"Start"}
            </Text>
          </Box>
        </Flex>

        <Box
          position="absolute"
          width="100%"
          display="flex"
          alignItems="center"
          justifyContent="flex-start"
          pointerEvents="none"
        >
          <SanityImagePreservingAspectRatio
            image={character1}
            resolution={1000}
            position="absolute"
            width={{ base: "18%", md: "13%" }}
            left={`${currentProgressPercentage}%`}
            transform={{ base: "translate(-50%, -5%) scale(-1, 1)", md: "translate(-50%, 20%) scale(-1, 1)" }}
            boxShadow="none"
            zIndex={2}
          />
          <SanityImagePreservingAspectRatio
            image={character2}
            resolution={1000}
            position="absolute"
            width={{ base: "27%", md: "19%" }}
            left={`${currentProgressPercentage}%`}
            transform={{ base: "translate(-40%, -15%) scale(-1, 1)", md: "translate(-40%, 0%) scale(-1, 1)" }}
            boxShadow="none"
            zIndex={1}
          />
        </Box>
        <Box width="100%">
          <SanityImagePreservingAspectRatio
            image={detail4}
            resolution={1000}
            width="100%"
            transform="translate(0, 45%)"
            boxShadow="none"
          />
        </Box>
        <Flex flexDirection="column">
          <Box height="7rem" marginTop="2rem" border={`1px solid #6D1F49`}></Box>
          <Box display="flex" flexDirection="column" alignItems="flex-end" justifyContent="flex-start">
            <Text position="absolute" fontSize="2xl">
              {"Mål"}
            </Text>
          </Box>
        </Flex>
      </Box>
      <Text>
        {tRich("I fjor leste dere i snitt <Bold>{dagligeSiderIFjor} sider</Bold> hver dag", {
          dagligeSiderIFjor: numberWithSpaces(dailyPagesLastYear),
          Bold,
        })}
        .
      </Text>
      <Flex flexDirection="row" justifyContent="flex-start" width="100%" flexWrap="wrap" aria-hidden>
        {dailyPagesLastYear > 0 &&
          Array.from({ length: dailyPagesLastYear }).map((value, index) => (
            <SanityImagePreservingAspectRatio
              key={index}
              image={detail9}
              resolution={200}
              width={{ base: "1.5rem", md: "2rem" }}
              boxShadow="none"
              zIndex={0}
              margin="0 -0.1rem"
            />
          ))}
      </Flex>
      <Text>
        {tRich("Hittil har dere lest i snitt <Bold>{dagligeSiderIÅr} sider</Bold> hver dag", {
          dagligeSiderIÅr: numberWithSpaces(dailyPagesThisYear),
          Bold,
        })}
        .
      </Text>
      <Flex flexDirection="row" justifyContent="flex-start" width="100%" flexWrap="wrap" aria-hidden>
        {dailyPagesThisYear > 0 &&
          Array.from({ length: dailyPagesThisYear }).map((value, index) => (
            <SanityImagePreservingAspectRatio
              key={index}
              image={detail10}
              resolution={200}
              width={{ base: "1.5rem", md: "2rem" }}
              boxShadow="none"
              zIndex={0}
              margin="0 -0.1rem"
            />
          ))}
      </Flex>
      {pagesReadDelta > 0 && (
        <>
          <Text>
            <Text as="span">
              {tRich(
                "For å slå fjorårets rekord må dere lese minst <Bold>{dagligeSiderForÅVinne} sider</Bold> hver dag",
                {
                  dagligeSiderForÅVinne: numberWithSpaces(dailyPagesToWin),
                  Bold,
                }
              )}
              {". "}
            </Text>
            <span>
              {dailyPagesDelta > 0 ? (
                <Text as="span">
                  {tRich("Det er <Bold>{dagligeSiderDelta}</Bold> mer enn dere leser nå!", {
                    dagligeSiderDelta: numberWithSpaces(dailyPagesDelta),
                    Bold,
                  })}
                </Text>
              ) : (
                t("Det betyr at dere er på stø kurs!")
              )}
            </span>
          </Text>
          <Flex flexDirection="row" justifyContent="flex-start" width="100%" flexWrap="wrap" aria-hidden>
            {dailyPagesToWin > 0 &&
              Array.from({ length: dailyPagesToWin }).map((value, index) => (
                <SanityImagePreservingAspectRatio
                  key={index}
                  image={detail11}
                  resolution={200}
                  width={{ base: "1.5rem", md: "2rem" }}
                  boxShadow="none"
                  zIndex={0}
                  margin="0 -0.1rem"
                />
              ))}
          </Flex>
        </>
      )}
    </CampaignViewSection>
  );
};
